//  Copyright (c) 2019 First Foundry LLC. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import moment from 'moment-timezone'
import { Table, TwoLine, withFixedHeaderDataTable } from 'components/Table'
import { DATE_FORMAT } from 'constants/Settings'
import { compose } from 'recompose'
import { get } from 'lodash'

const StandardHorizontalLine = styled.div`
  height: 1px;
  width: 1000px;
  border: 1px dashed #979797;
  margin-left: 4px;
`

const SmallHorizontalLine = styled.div`
  height: 1px;
  width: 16px;
  border: 1px dashed #979797;
  margin-right: 11px;
`

const VerticalLine = styled.div`
  height: auto;
  width: 1px;
  border: 1px dashed #979797;
  margin-top: 13px;
  margin-bottom: 29px;
`

// eslint-disable-next-line react/prop-types
const StyledTwoLine = ({ topValue, bottomValue, isLink = false, target }) => (
  <TwoLine
    topStyle={{ fontWeight: 'bold', fontSize: '11px' }}
    top={`${topValue}`.toUpperCase()}
    bottomStyle={{ fontSize: '13px' }}
    bottom={isLink ? <a href={`${target}`} style={{ textDecoration: 'underline' }}>{`${bottomValue}`}</a> : `${bottomValue}`}
  />
)

const StyledCellWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const COLUMNS = storeTimezone => [{
  Header: 'time',
  accessor: 'timestamp',
  sortMethod: (a, b) => {
    try {
      if (!a && !b) return 0
      if (!a && b) return 1
      if (!b && a) return -1
      return new Date(b) - new Date(a)
    } catch (error) {
      if (a === b) return 0
      if (!a && !b) return 0
      if (!a && b) return 1
      if (!b && a) return -1
      if (a > b) return 1
      return -1
    }
  },
  // eslint-disable-next-line react/prop-types
  Cell: ({ original }) => (
    <StyledCellWrapper>
      <StyledTwoLine
        topValue={moment.utc(original.timestamp).tz(storeTimezone).format(DATE_FORMAT)}
        bottomValue={moment.utc(original.timestamp).tz(storeTimezone).format('hh:mm A')}
      />
      <StandardHorizontalLine />
    </StyledCellWrapper>
  ),
  minWidth: 20,
},
{
  Header: 'user',
  accessor: 'employeeName',
  // eslint-disable-next-line react/prop-types
  Cell: ({ value, original }) => (
    <StyledCellWrapper>
      <StyledTwoLine
        topValue={original.employeeId}
        bottomValue={value}
        target={`/employees/${original.employeeId}/edit`}
        isLink
      />
      <StandardHorizontalLine />
    </StyledCellWrapper>
  ),
},
{
  Header: 'changes made',
  accessor: 'payload',
  // eslint-disable-next-line react/prop-types
  Cell: ({ original }) => (
    <div style={{ display: 'flex', flexDirection: 'row' }}>
      <VerticalLine />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {original.payload.map(item => item.map(val => (
          <div
            key={`${val.header}${item.indexOf(val)}`}
            style={{ marginBottom: '16px' }}
          >
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <SmallHorizontalLine />
              <StyledTwoLine
                topValue={`${get(val, 'header')}`}
                bottomValue={`${get(val, 'content')}`}
              />
            </div>
          </div>
        )))}
      </div>
    </div>
  ),
}]

const DEFAULT_SORTING = [{
  id: 'timestamp',
  desc: false,
}]

// eslint-disable-next-line react/prop-types
const PriceGroupLogsTable = ({ data, storeTimezone }) => (
  <Table
    data={data}
    columns={COLUMNS(storeTimezone)}
    defaultSorted={DEFAULT_SORTING}
    getTrProps={() => ({
          style: {
            display: 'flex',
            alignItems: 'flex-start',
            paddingTop: '15px',
            paddingBottom: '15px',
            height: 'auto',
          },
        })}
  />
)

PriceGroupLogsTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  storeTimezone: PropTypes.string,
}

export default compose(
  withFixedHeaderDataTable(60, {
    width: 'calc(100% - 280px)',
  }),
)(PriceGroupLogsTable)
